/** 激励广告奖励 / 置顶获取模式 */
export type AdRewardMode = 'pin' | 'refresh' | '';

/** 置顶获取方式：激励广告 / 转发 / 客服 */
export type PinAcquireMethod = 'ad' | 'share' | 'service' | '';

export type AdRewardCopyConfig = {
  pin_owner_title: string;
  pin_owner_body: string;
  pin_owner_confirm: string;
  pin_owner_cancel: string;
  pin_owner_success: string;
  pin_owner_exhausted_btn: string;
  pin_owner_exhausted_title: string;
  pin_owner_exhausted_body: string;
  pin_other_title: string;
  pin_other_body: string;
  pin_other_confirm: string;
  pin_other_cancel: string;
  pin_other_success: string;
  share_owner_title: string;
  share_owner_body: string;
  share_owner_btn: string;
  share_owner_progress: string;
  share_owner_success: string;
  share_owner_done_hint: string;
  share_modal_btn: string;
  service_owner_title: string;
  service_owner_body: string;
  service_owner_btn: string;
  refresh_owner_title: string;
  refresh_owner_body: string;
  refresh_owner_auto_body: string;
  refresh_owner_confirm: string;
  refresh_owner_cancel: string;
  refresh_owner_success: string;
  refresh_owner_exhausted_btn: string;
  refresh_owner_exhausted_title: string;
  refresh_owner_exhausted_body: string;
  refresh_other_title: string;
  refresh_other_body: string;
  refresh_other_confirm: string;
  refresh_other_cancel: string;
  refresh_other_success: string;
};

export type AdRewardConfig = {
  mode: AdRewardMode;
  /** 置顶获取方式；mode=pin 时生效 */
  pin_method: PinAcquireMethod;
  pin_owner_enabled: boolean;
  pin_other_enabled: boolean;
  /** 每次完整观看激励视频赠送的置顶天数（可叠加） */
  pin_reward_days: number;
  /** 发布人每日自己看广告置顶次数，0 表示不限 */
  pin_owner_daily_limit: number;
  /** 每人每日帮他人置顶次数，0 表示不限 */
  pin_other_daily_limit: number;
  /** 转发送置顶：通过发布人转发打开的去重人数 */
  share_open_required: number;
  share_pin_days: number;
  /** 是否仅统计平台新用户打开 */
  share_require_new_user: boolean;
  service_qr_path: string;
  service_title: string;
  service_body: string;
  refresh_owner_enabled: boolean;
  refresh_other_enabled: boolean;
  /** 发布人本人进入详情页超过该小时数后，可自动引导刷新（仅 refresh 组） */
  refresh_owner_auto_hours: number;
  /** 发布人每日自己看广告刷新次数，0 表示不限 */
  refresh_owner_daily_limit: number;
  /** 每人每日帮他人刷新次数，0 表示不限 */
  refresh_other_daily_limit: number;
  copy: AdRewardCopyConfig;
};

/** 信息类型上的置顶方式覆盖 */
export type TypePinOverride = {
  pin_override?: boolean;
  pin_method?: PinAcquireMethod;
  pin_owner_enabled?: boolean;
  pin_other_enabled?: boolean;
  pin_reward_days?: number;
  pin_owner_daily_limit?: number;
  pin_other_daily_limit?: number;
  share_open_required?: number;
  share_pin_days?: number;
  share_require_new_user?: boolean;
  service_qr_path?: string;
  service_title?: string;
  service_body?: string;
};

export function defaultAdRewardCopy(): AdRewardCopyConfig {
  return {
    pin_owner_title: '置顶推广',
    pin_owner_body:
      '完整观看激励视频，本条信息将置顶展示 {days} 天，多次观看可叠加，让更多人看到。',
    pin_owner_confirm: '观看视频',
    pin_owner_cancel: '暂不需要',
    pin_owner_success: '已置顶 {days} 天',
    pin_owner_exhausted_btn: '今日次数已用完，转发求助',
    pin_owner_exhausted_title: '今日置顶次数已用完',
    pin_owner_exhausted_body:
      '今日自行置顶次数已用完。转发给亲友，请他们打开详情后点击「帮 TA 置顶」即可助力。',
    pin_other_title: '帮 TA 置顶',
    pin_other_body: '完整观看激励视频，可帮发布者将本条信息置顶 {days} 天。您今日还可助力 {remaining} 次（共 {limit} 次/日）。',
    pin_other_confirm: '观看视频',
    pin_other_cancel: '取消',
    pin_other_success: '已帮 TA 置顶 {days} 天',
    share_owner_title: '恭喜：获免费置顶机会',
    share_owner_body:
      '将信息转发亲友群/小区群，累积 {required} 人打开，即可置顶 {days} 天。',
    share_owner_btn: '转发得置顶',
    share_owner_progress: '已邀请 {current}/{required} 人打开',
    share_owner_success: '已置顶 {days} 天，可继续转发叠加',
    share_owner_done_hint: '再邀请 {remaining} 人打开即可置顶',
    share_modal_btn: '点此转发',
    service_owner_title: '联系客服置顶',
    service_owner_body: '长按识别二维码添加客服微信，发送本条信息编号即可申请置顶。',
    service_owner_btn: '联系客服置顶',
    refresh_owner_title: '刷新信息',
    refresh_owner_body:
      '完整观看激励视频，可免费刷新本条信息排序，让信息排到更靠前，被更多人看到。',
    refresh_owner_auto_body:
      '信息已发布超过 {hours} 小时。完整观看激励视频，可免费刷新排序，让更多人看到。',
    refresh_owner_confirm: '观看视频',
    refresh_owner_cancel: '暂不需要',
    refresh_owner_success: '刷新成功',
    refresh_owner_exhausted_btn: '今日次数已用完，转发求助',
    refresh_owner_exhausted_title: '今日刷新次数已用完',
    refresh_owner_exhausted_body:
      '今日自行刷新次数已用完。转发给亲友，请他们打开详情后点击「帮 TA 刷新」即可助力。',
    refresh_other_title: '帮 TA 刷新',
    refresh_other_body: '完整观看激励视频，可帮发布者刷新信息排序。您今日还可助力 {remaining} 次（共 {limit} 次/日）。',
    refresh_other_confirm: '观看视频',
    refresh_other_cancel: '取消',
    refresh_other_success: '已帮 TA 刷新',
  };
}

export function defaultAdReward(): AdRewardConfig {
  return {
    mode: '',
    pin_method: 'ad',
    pin_owner_enabled: false,
    pin_other_enabled: false,
    pin_reward_days: 3,
    pin_owner_daily_limit: 3,
    pin_other_daily_limit: 5,
    share_open_required: 10,
    share_pin_days: 3,
    share_require_new_user: false,
    service_qr_path: '',
    service_title: '联系客服置顶',
    service_body: '长按识别二维码添加客服微信，发送本条信息编号即可申请置顶。',
    refresh_owner_enabled: false,
    refresh_other_enabled: false,
    refresh_owner_auto_hours: 24,
    refresh_owner_daily_limit: 3,
    refresh_other_daily_limit: 5,
    copy: defaultAdRewardCopy(),
  };
}

function clampInt(n: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(n)));
}

function trimCopyField(raw: unknown, fallback: string, maxLen: number): string {
  const s = raw != null ? String(raw).trim() : '';
  const v = s !== '' ? s : fallback;
  if (v.length <= maxLen) return v;
  return v.slice(0, maxLen);
}

export function normalizeAdRewardCopy(raw: unknown): AdRewardCopyConfig {
  const d = defaultAdRewardCopy();
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out = { ...d };
  (Object.keys(d) as (keyof AdRewardCopyConfig)[]).forEach((k) => {
    out[k] = trimCopyField(o[k], d[k], k.includes('_body') ? 512 : 64);
  });
  return out;
}

export function normalizePinMethod(raw: unknown): PinAcquireMethod {
  const s = String(raw ?? '').trim();
  if (s === 'ad' || s === 'share' || s === 'service') return s;
  return '';
}

export function normalizeAdReward(raw: unknown): AdRewardConfig {
  const d = defaultAdReward();
  if (!raw || typeof raw !== 'object') return d;
  const o = raw as Record<string, unknown>;
  const pinDays = clampInt(Number(o.pin_reward_days), 1, 30, d.pin_reward_days);
  const autoHours = clampInt(Number(o.refresh_owner_auto_hours), 1, 168, d.refresh_owner_auto_hours);
  const pinDaily = clampInt(Number(o.pin_owner_daily_limit), 0, 99, d.pin_owner_daily_limit);
  const pinHelpDaily = clampInt(Number(o.pin_other_daily_limit), 0, 99, d.pin_other_daily_limit);
  const refDaily = clampInt(Number(o.refresh_owner_daily_limit), 0, 99, d.refresh_owner_daily_limit);
  const refHelpDaily = clampInt(Number(o.refresh_other_daily_limit), 0, 99, d.refresh_other_daily_limit);
  const shareNeed = clampInt(Number(o.share_open_required), 1, 999, d.share_open_required);
  const shareDays = clampInt(Number(o.share_pin_days), 1, 30, d.share_pin_days);
  const shareRequireNew = !!o.share_require_new_user;
  const serviceQr = String(o.service_qr_path ?? '').trim().slice(0, 512);
  const serviceTitle = trimCopyField(o.service_title, d.service_title, 64);
  const serviceBody = trimCopyField(o.service_body, d.service_body, 512);
  const copy = normalizeAdRewardCopy(o.copy);

  let pinOwner = !!o.pin_owner_enabled;
  let pinOther = !!o.pin_other_enabled;
  const refOwner = !!o.refresh_owner_enabled;
  const refOther = !!o.refresh_other_enabled;
  let pinMethod = normalizePinMethod(o.pin_method);
  if ((pinOwner || pinOther) && pinMethod === '') pinMethod = 'ad';
  const pinViaShareOrService = pinMethod === 'share' || pinMethod === 'service';
  if (pinViaShareOrService) {
    pinOwner = true;
    pinOther = false;
  }

  const extras = {
    pin_method: pinMethod as PinAcquireMethod,
    share_open_required: shareNeed,
    share_pin_days: shareDays,
    share_require_new_user: shareRequireNew,
    service_qr_path: serviceQr,
    service_title: serviceTitle,
    service_body: serviceBody,
  };

  if (pinOwner || pinOther || pinViaShareOrService) {
    const method: PinAcquireMethod =
      pinMethod === 'share' || pinMethod === 'service' || pinMethod === 'ad' ? pinMethod : 'ad';
    return {
      mode: 'pin',
      pin_owner_enabled: method === 'ad' ? pinOwner : true,
      pin_other_enabled: method === 'ad' ? pinOther : false,
      pin_reward_days: pinDays,
      pin_owner_daily_limit: pinDaily,
      pin_other_daily_limit: pinHelpDaily,
      refresh_owner_enabled: false,
      refresh_other_enabled: false,
      refresh_owner_auto_hours: autoHours,
      refresh_owner_daily_limit: refDaily,
      refresh_other_daily_limit: refHelpDaily,
      copy,
      ...extras,
      pin_method: method,
    };
  }
  if (refOwner || refOther) {
    return {
      mode: 'refresh',
      pin_owner_enabled: false,
      pin_other_enabled: false,
      pin_reward_days: pinDays,
      pin_owner_daily_limit: pinDaily,
      pin_other_daily_limit: pinHelpDaily,
      refresh_owner_enabled: refOwner,
      refresh_other_enabled: refOther,
      refresh_owner_auto_hours: autoHours,
      refresh_owner_daily_limit: refDaily,
      refresh_other_daily_limit: refHelpDaily,
      copy,
      ...extras,
      pin_method: '',
    };
  }
  return {
    ...d,
    pin_method: '',
    pin_reward_days: pinDays,
    pin_owner_daily_limit: pinDaily,
    pin_other_daily_limit: pinHelpDaily,
    share_open_required: shareNeed,
    share_pin_days: shareDays,
    share_require_new_user: shareRequireNew,
    service_qr_path: serviceQr,
    service_title: serviceTitle,
    service_body: serviceBody,
    refresh_owner_auto_hours: autoHours,
    refresh_owner_daily_limit: refDaily,
    refresh_other_daily_limit: refHelpDaily,
    copy,
  };
}

export function adRewardGroupLabel(mode: AdRewardMode): string {
  if (mode === 'pin') return '置顶组（多种获取方式）';
  if (mode === 'refresh') return '刷新组（激励视频送信息刷新）';
  return '未开启';
}

export function pinMethodLabel(method: PinAcquireMethod): string {
  if (method === 'ad') return '激励广告置顶';
  if (method === 'share') return '转发送置顶';
  if (method === 'service') return '联系客服置顶';
  return '未设置';
}
